import { Component } from '@theme/component';
import { onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';

const desktopMenuMediaQuery = window.matchMedia('(min-width: 990px)');

/**
 * Keeps shared header dimensions and responsive mode in sync.
 * Sticky behavior is loaded separately only when the setting is enabled.
 */
class HeaderComponent extends Component {
  /** @type {(() => void) | undefined} */
  #disconnectSticky;

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const borderBoxSize = entry.borderBoxSize?.[0] ?? entry.borderBoxSize;
      const height = borderBoxSize?.blockSize ?? entry.contentRect.height;
      if (entry.target === this) {
        document.body.style.setProperty('--header-height', `${Math.round(height)}px`);
      } else {
        this.style.setProperty('--top-row-height', `${height}px`);
      }
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    if (this.hasAttribute('data-real-split-underlay')) {
      const topRow = this.querySelector('.header__row--top');
      if (topRow) this.#resizeObserver.observe(topRow, { box: 'border-box' });
    }
    desktopMenuMediaQuery.addEventListener('change', setHeaderMenuStyle);
    setHeaderMenuStyle();

    if (this.hasAttribute('sticky')) {
      import('./header-sticky.js').then(({ setupStickyHeader }) => {
        if (this.isConnected) this.#disconnectSticky = setupStickyHeader(this);
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    desktopMenuMediaQuery.removeEventListener('change', setHeaderMenuStyle);
    this.#disconnectSticky?.();
    this.#disconnectSticky = undefined;
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

// Anchor the shopify-account dialog to the bottom of the account button itself.
document.addEventListener(
  'open',
  (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || !target.matches('shopify-account')) return;

    const bottom = Math.max(0, Math.round(target.getBoundingClientRect().bottom));
    target.style.setProperty('--account-dialog-top', `${bottom}px`);
  },
  true
);

onDocumentLoaded(() => {
  const headerGroup = document.querySelector('#header-group');
  if (!(headerGroup instanceof HTMLElement)) return;

  /** @type {Map<Element, number>} */
  const observedBlockSizes = new Map();

  const updateHeaderGroupHeight = () => {
    const directChildren = Array.from(headerGroup.children).filter((element) => element instanceof HTMLElement);
    if (directChildren.some((element) => !observedBlockSizes.has(element))) return;

    const headerGroupHeight = directChildren.reduce(
      (totalHeight, element) => totalHeight + (observedBlockSizes.get(element) ?? 0),
      0
    );

    document.body.style.setProperty('--header-group-height', `${Math.round(headerGroupHeight)}px`);
  };

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const borderBoxSize = entry.borderBoxSize?.[0] ?? entry.borderBoxSize;
      observedBlockSizes.set(entry.target, borderBoxSize?.blockSize ?? entry.contentRect.height);
    }
    updateHeaderGroupHeight();
  });

  const observeHeaderGroupChildren = () => {
    for (const element of headerGroup.children) {
      if (element instanceof HTMLElement) resizeObserver.observe(element);
    }
  };

  observeHeaderGroupChildren();
  // Section nodes only change without navigation in the theme editor.
  if (window.Shopify?.designMode) {
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.removedNodes) {
          if (node instanceof Element) observedBlockSizes.delete(node);
        }
      }
      observeHeaderGroupChildren();
    }).observe(headerGroup, { childList: true });
  }
});
